import React, { useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { Button } from "@jobber/components/Button";
import { Heading } from "@jobber/components/Heading";
import { InputText } from "@jobber/components/InputText";
import { showToast } from "@jobber/components/Toast";
import { Spinner } from "@jobber/components/Spinner";
import { apiGet, apiPost, apiPut } from "../../../api-client";

const ACTION_ORDER = ["view", "create", "update", "delete", "manage"];
const RESOURCE_ORDER = [
  "clients",
  "quotes",
  "quotes_preparation",
  "quote_fix",
  "visits",
  "jobs",
  "job_automation",
  "approved_quotes",
  "notes",
  "settings",
];
const RESOURCE_LABELS = {
  jobs: "Closed Jobs",
};

const initialForm = {
  fullName: "",
  email: "",
  mode: "preset",
  preset: "worker",
  permissions: {},
};

const normalizePermissionMap = (permissionMap = {}) => {
  if (!permissionMap || typeof permissionMap !== "object" || Array.isArray(permissionMap)) {
    return {};
  }

  const normalized = {};
  Object.entries(permissionMap).forEach(([resource, actions]) => {
    if (!Array.isArray(actions)) {
      return;
    }

    const validActions = Array.from(
      new Set(
        actions
          .map((action) => String(action).toLowerCase().trim())
          .filter((action) => ACTION_ORDER.includes(action))
      )
    );

    if (validActions.length > 0) {
      if (validActions.some((action) => action !== "view") && !validActions.includes("view")) {
        validActions.push("view");
      }

      normalized[resource] = validActions.sort((a, b) => ACTION_ORDER.indexOf(a) - ACTION_ORDER.indexOf(b));
    }
  });

  return normalized;
};

const permissionsEqual = (a = {}, b = {}) => {
  const left = normalizePermissionMap(a);
  const right = normalizePermissionMap(b);
  return JSON.stringify(left) === JSON.stringify(right);
};

const titleCase = (value = "") =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getResourceLabel = (resource = "") => RESOURCE_LABELS[resource] || titleCase(resource);

const getFullName = (person = {}) => `${person?.firstName || ""} ${person?.lastName || ""}`.trim();

const splitFullName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || "",
  };
};

const getRoleLabel = (role = "") => (role === "custom" ? "Custom access" : titleCase(role || "worker"));

const PERMISSION_LEVELS = [
  { key: "none", label: "No access", actions: [] },
  { key: "view", label: "View", actions: ["view"] },
  { key: "create", label: "View + Create", actions: ["view", "create"] },
  { key: "update", label: "View + Create + Update", actions: ["view", "create", "update"] },
  { key: "delete", label: "View + Create + Update + Delete", actions: ["view", "create", "update", "delete"] },
  { key: "manage", label: "Manage", actions: ["view", "create", "update", "delete", "manage"] },
];

const getPermissionLevelOptions = (availableActions = []) => {
  const availableSet = new Set(availableActions);
  const options = [PERMISSION_LEVELS[0]];
  const cumulativeActions = [];

  ACTION_ORDER.forEach((action) => {
    if (!availableSet.has(action)) {
      return;
    }

    cumulativeActions.push(action);
    options.push({
      key: action,
      label: action === "manage" ? "Manage" : cumulativeActions.map(titleCase).join(" + "),
      actions: [...cumulativeActions],
    });
  });

  return options;
};

const getEnabledPermissionLevelOptions = (availableActions = []) =>
  getPermissionLevelOptions(availableActions).filter((level) => level.key !== "none");

const getFirstEnabledPermissionLevel = (availableActions = []) =>
  getEnabledPermissionLevelOptions(availableActions)[0]?.key || "none";

const getActionsForLevel = (levelKey, availableActions = []) => {
  const level = PERMISSION_LEVELS.find((item) => item.key === levelKey) || PERMISSION_LEVELS[0];
  const availableSet = new Set(availableActions);

  if (level.key === "manage") {
    return ACTION_ORDER.filter((action) => availableSet.has(action));
  }

  return level.actions
    .filter((action) => availableSet.has(action))
    .sort((a, b) => ACTION_ORDER.indexOf(a) - ACTION_ORDER.indexOf(b));
};

const getPermissionLevel = (selectedActions = [], availableActions = []) => {
  const normalizedSelected = normalizePermissionMap({ current: selectedActions }).current || [];
  const selectedKey = JSON.stringify(normalizedSelected);
  const options = getPermissionLevelOptions(availableActions);
  const exactMatch = options.find((option) => JSON.stringify(option.actions) === selectedKey);

  if (exactMatch) {
    return exactMatch.key;
  }

  if (!normalizedSelected.length) {
    return "none";
  }

  const highestSelectedAction = [...normalizedSelected]
    .reverse()
    .find((action) => options.some((option) => option.key === action));

  return highestSelectedAction || getFirstEnabledPermissionLevel(availableActions);
};

const getLevelDescription = (actions = []) => {
  if (!actions.length) {
    return "No access";
  }

  return actions.map(titleCase).join(", ");
};

const inferPresetFromPermissions = (permissions = {}, presets = {}) => {
  const normalizedCurrent = normalizePermissionMap(permissions);
  const presetKeys = Object.keys(presets || {});

  for (const key of presetKeys) {
    const normalizedPreset = normalizePermissionMap(presets[key]);
    if (permissionsEqual(normalizedCurrent, normalizedPreset)) {
      return key;
    }
  }

  return "custom";
};

const getDefaultPresetKey = (presets = {}) => {
  if (presets?.worker) {
    return "worker";
  }

  const [firstPreset] = Object.keys(presets || {});
  return firstPreset || "custom";
};

const buildDefaultForm = (presets = {}, mode = "preset") => {
  const preset = getDefaultPresetKey(presets);
  const permissions = preset === "custom" ? {} : normalizePermissionMap(presets?.[preset] || {});

  return {
    ...initialForm,
    mode,
    preset,
    permissions,
  };
};

const sortResources = (resources = []) => {
  const orderIndex = new Map(RESOURCE_ORDER.map((resource, index) => [resource, index]));

  return [...resources].sort((a, b) => {
    const aIndex = orderIndex.has(a) ? orderIndex.get(a) : Number.POSITIVE_INFINITY;
    const bIndex = orderIndex.has(b) ? orderIndex.get(b) : Number.POSITIVE_INFINITY;

    if (aIndex === bIndex) {
      return a.localeCompare(b);
    }

    return aIndex - bIndex;
  });
};

function AccessControl() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [access, setAccess] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deactivatingMemberId, setDeactivatingMemberId] = useState(null);

  const canManageMembers = useMemo(() => Boolean(access?.can?.manageMembers), [access]);
  const canDeactivateMembers = useMemo(
    () => canManageMembers && ["owner", "admin"].includes(access?.membership?.role),
    [access?.membership?.role, canManageMembers]
  );
  const availablePresets = useMemo(() => access?.presets || {}, [access]);

  const resourceActionMap = useMemo(() => {
    const map = {};
    Object.values(availablePresets).forEach((presetPermissions) => {
      const normalized = normalizePermissionMap(presetPermissions);
      Object.entries(normalized).forEach(([resource, actions]) => {
        map[resource] = Array.from(new Set([...(map[resource] || []), ...actions])).sort(
          (a, b) => ACTION_ORDER.indexOf(a) - ACTION_ORDER.indexOf(b)
        );
      });
    });

    const normalizedCurrent = normalizePermissionMap(form.permissions);
    Object.entries(normalizedCurrent).forEach(([resource, actions]) => {
      map[resource] = Array.from(new Set([...(map[resource] || []), ...actions])).sort(
        (a, b) => ACTION_ORDER.indexOf(a) - ACTION_ORDER.indexOf(b)
      );
    });

    return map;
  }, [availablePresets, form.permissions]);

  const currentResources = useMemo(() => sortResources(Object.keys(resourceActionMap)), [resourceActionMap]);

  const loadData = async () => {
    try {
      setLoading(true);
      const accessResult = await apiGet("organization/access/me");
      if (accessResult?.status) {
        setAccess(accessResult.data);
        setForm((prev) => ({
          ...buildDefaultForm(accessResult?.data?.presets || {}, prev.mode),
          fullName: prev.fullName,
          email: prev.email,
        }));
      }

      const membersResult = await apiGet("organization/members");
      if (membersResult?.status) {
        setMembers(membersResult.data || []);
      }

      if (accessResult?.data?.can?.manageMembers) {
        const invitationResult = await apiGet("organization/invitations");
        if (invitationResult?.status) {
          setInvitations(invitationResult.data || []);
        }
      }
    } catch (error) {
      showToast({ message: error.message || "Failed to load access control data", variation: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const setValue = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetMemberForm = () => {
    setForm(buildDefaultForm(availablePresets));
    setEditingMember(null);
    setShowMemberForm(false);
  };

  const isCurrentMember = (member) => {
    const currentMembershipId = access?.membership?._id;
    const currentUserId = access?.membership?.userId?._id || access?.membership?.userId;

    return (
      String(member?._id || "") === String(currentMembershipId || "") ||
      String(member?.user?._id || "") === String(currentUserId || "")
    );
  };

  const canEditMember = (member) => canManageMembers && member?.role !== "owner" && !isCurrentMember(member);

  const canDeactivateMember = (member) =>
    canDeactivateMembers && member?.role !== "owner" && !isCurrentMember(member);

  const setPreset = (presetName) => {
    if (presetName === "custom") {
      setForm((prev) => ({ ...prev, preset: "custom", mode: "preset" }));
      return;
    }

    const presetPermissions = normalizePermissionMap(availablePresets?.[presetName] || {});
    setForm((prev) => ({
      ...prev,
      mode: "preset",
      preset: presetName,
      permissions: presetPermissions,
    }));
  };

  const setPermissionLevel = (resource, level) => {
    setForm((prev) => {
      const current = normalizePermissionMap(prev.permissions);
      const updatedActions = getActionsForLevel(level, resourceActionMap[resource] || []);

      const updatedPermissions = { ...current };
      if (updatedActions.length === 0) {
        delete updatedPermissions[resource];
      } else {
        updatedPermissions[resource] = updatedActions;
      }

      const selectedPresetPermissions = normalizePermissionMap(availablePresets?.[prev.preset] || {});
      const shouldSwitchToCustom = prev.preset !== "custom" && !permissionsEqual(updatedPermissions, selectedPresetPermissions);

      return {
        ...prev,
        permissions: updatedPermissions,
        preset: shouldSwitchToCustom ? "custom" : prev.preset,
      };
    });
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    if (!editingMember) {
      const { firstName, lastName } = splitFullName(form.fullName);
      if (!firstName || !lastName) {
        showToast({ message: "Please enter the user's first and last name", variation: "error" });
        return;
      }
    }

    const payload = {
      email: form.email,
      isAdmin: form.mode === "admin",
      preset: form.mode === "admin" || form.preset === "custom" ? undefined : form.preset,
      permissions: form.mode === "admin" ? undefined : normalizePermissionMap(form.permissions),
    };

    if (!editingMember) {
      const { firstName, lastName } = splitFullName(form.fullName);
      payload.firstName = firstName;
      payload.lastName = lastName;
    }

    try {
      setSaving(true);
      const result = editingMember
        ? await apiPut(`organization/members/${editingMember._id}`, payload)
        : await apiPost("organization/invite-member", payload);
      if (result?.status) {
        if (editingMember) {
          showToast({ message: "Member permissions updated", variation: "success" });
        } else {
          const inviteLink = result?.data?.inviteLink;
          showToast({ message: "Invitation created", variation: "success" });
          if (inviteLink && navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(inviteLink);
            showToast({ message: "Invite link copied", variation: "success" });
          }
        }
        resetMemberForm();
        await loadData();
      }
    } catch (error) {
      showToast({ message: error.message || "Failed to save member", variation: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deactivateMemberHandler = async (member) => {
    const memberName = getFullName(member?.user) || member?.user?.email || "this member";
    const confirmed = window.confirm(`Deactivate ${memberName}? They will lose access to this organization.`);

    if (!confirmed) {
      return;
    }

    try {
      setDeactivatingMemberId(member._id);
      const result = await apiPost(`organization/members/${member._id}/deactivate`);
      if (result?.status) {
        showToast({ message: "Member deactivated", variation: "success" });
        if (editingMember?._id === member._id) {
          resetMemberForm();
        }
        await loadData();
      }
    } catch (error) {
      showToast({ message: error.message || "Failed to deactivate member", variation: "error" });
    } finally {
      setDeactivatingMemberId(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-3">
        <Spinner size="base" />
      </div>
    );
  }

  return (
    <div className="access-control-page">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <Heading level={3}>Organization Access Control</Heading>
          <div className="text-muted mt-1">{access?.organization?.name || "N/A"}</div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <span className="badge rounded-pill bg-light text-dark border">{members.length} members</span>
          <span className="badge rounded-pill bg-light text-dark border">{invitations.length} pending invites</span>
          <span className="badge rounded-pill bg-primary">{getRoleLabel(access?.membership?.role)}</span>
        </div>
      </div>

      {canManageMembers ? (
        <div className="card p-3 mb-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <Heading level={4}>{editingMember ? "Edit Member Access" : "Invite A User"}</Heading>
              <div className="text-muted small mt-1">
                {editingMember ? "Update this member's role and permissions." : "Add their name, email, and the access they should receive."}
              </div>
            </div>
            {!showMemberForm ? (
              <Button
                label="Invite user"
                icon="plus"
                onClick={() => {
                  setEditingMember(null);
                  setForm(buildDefaultForm(availablePresets));
                  setShowMemberForm(true);
                }}
              />
            ) : null}
          </div>

          {showMemberForm ? (
            <form onSubmit={submitHandler} className="mt-3">
              <div className="row g-3">
                {!editingMember ? (
                  <div className="col-md-6">
                    <InputText
                      value={form.fullName}
                      placeholder="Full name"
                      onChange={(value) => setValue("fullName", value)}
                    />
                  </div>
                ) : null}
                <div className={editingMember ? "col-md-12" : "col-md-6"}>
                  <InputText
                    value={form.email}
                    placeholder="Email"
                    onChange={(value) => setValue("email", value)}
                    disabled={Boolean(editingMember)}
                  />
                </div>
              </div>

              <div className="mt-3 d-flex flex-wrap gap-2">
                <label className={`border rounded px-3 py-2 pointer ${form.mode === "admin" ? "bg-white border-primary" : "bg-light"}`}>
                  <input
                    className="me-2"
                    type="radio"
                    name="mode"
                    value="admin"
                    checked={form.mode === "admin"}
                    onChange={(e) => setValue("mode", e.target.value)}
                  />
                  Admin
                </label>
                <label className={`border rounded px-3 py-2 pointer ${form.mode === "preset" ? "bg-white border-primary" : "bg-light"}`}>
                  <input
                    className="me-2"
                    type="radio"
                    name="mode"
                    value="preset"
                    checked={form.mode === "preset"}
                    onChange={(e) => setValue("mode", e.target.value)}
                  />
                  Role based
                </label>
              </div>

              {form.mode !== "admin" && (
                <div className="mt-3">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-5">
                      <label className="form-label fw-semibold">Role template</label>
                      <select
                        className="form-select"
                        value={form.preset}
                        onChange={(e) => setPreset(e.target.value)}
                      >
                        {Object.keys(availablePresets).map((presetKey) => (
                          <option key={presetKey} value={presetKey}>
                            {titleCase(presetKey)}
                          </option>
                        ))}
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="col-md-7 text-muted small">
                      Choose a template first, then fine-tune individual permissions below.
                    </div>
                  </div>

                  <div className="row g-2 mt-2">
                    {currentResources.map((resource) => {
                      const availableActions = resourceActionMap[resource] || [];
                      const selectedActions = form.permissions?.[resource] || [];
                      const selectedLevel = getPermissionLevel(selectedActions, availableActions);
                      const enabledOptions = getEnabledPermissionLevelOptions(availableActions);
                      const isEnabled = selectedLevel !== "none";

                      return (
                        <div className="col-lg-6" key={resource}>
                          <div className="border rounded p-3 h-100 bg-white">
                            <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                              <div className="fw-semibold">{getResourceLabel(resource)}</div>
                              <div className="form-check form-switch m-0">
                                <input
                                  className="form-check-input pointer"
                                  type="checkbox"
                                  role="switch"
                                  checked={isEnabled}
                                  onChange={(event) => setPermissionLevel(
                                    resource,
                                    event.target.checked ? getFirstEnabledPermissionLevel(availableActions) : "none"
                                  )}
                                />
                              </div>
                            </div>

                            {isEnabled ? (
                              <div className="d-grid gap-2">
                                {enabledOptions.map((level) => (
                                  <label
                                    key={`${resource}-${level.key}`}
                                    className={`d-flex align-items-start gap-2 ${selectedLevel === level.key ? "fw-semibold" : "text-muted"}`}
                                  >
                                    <input
                                      type="radio"
                                      name={`permission-${resource}`}
                                      checked={selectedLevel === level.key}
                                      onChange={() => setPermissionLevel(resource, level.key)}
                                    />
                                    <span>
                                      <span className="d-block">{level.label} {getResourceLabel(resource)}</span>
                                      {/* <small className="fw-normal">{getLevelDescription(level.actions)}</small> */}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="text-muted small">Turn on access to choose a permission level.</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {form.preset === "custom" && (
                    <small className="text-muted d-block mt-2">Permissions were edited, so this access is now custom.</small>
                  )}
                </div>
              )}

              <div className="mt-3 d-flex gap-2">
                {saving ? <Spinner size="base" /> : <Button label={editingMember ? "Save changes" : "Send invite"} submit />}
                <Button label="Cancel" type="secondary" onClick={resetMemberForm} />
              </div>
            </form>
          ) : null}
        </div>
      ) : (
        <Alert variant="warning">You can view members but cannot manage them.</Alert>
      )}

      <div className="row g-4">
        <div className="col-lg-7">
          <div className="card p-3 h-100">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Heading level={4}>Members</Heading>
              <span className="text-muted small">{members.length} active</span>
            </div>
            <div className="list-group list-group-flush">
              {members.map((member) => {
                const memberName = getFullName(member?.user) || member?.user?.email || "Unnamed user";
                const currentMember = isCurrentMember(member);
                return (
                  <div key={member._id} className="list-group-item px-0 d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <div className="fw-semibold">{memberName}</div>
                        {currentMember ? <span className="badge rounded-pill bg-primary">You</span> : null}
                      </div>
                      <div className="text-muted small">{member?.user?.email}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge rounded-pill bg-light text-dark border">{getRoleLabel(member?.role)}</span>
                      {canEditMember(member) ? (
                        <Button
                          label="Edit"
                          type="secondary"
                          size="small"
                          onClick={() => {
                            const mode = member?.role === "admin" ? "admin" : "preset";
                            const preset = mode === "admin"
                              ? "worker"
                              : inferPresetFromPermissions(member?.permissions || {}, availablePresets);
                            setEditingMember(member);
                            setForm({
                              fullName: getFullName(member?.user),
                              email: member?.user?.email || "",
                              mode,
                              preset,
                              permissions:
                                mode === "admin"
                                  ? normalizePermissionMap(availablePresets?.worker || {})
                                  : normalizePermissionMap(member?.permissions || {}),
                            });
                            setShowMemberForm(true);
                          }}
                        />
                      ) : null}
                      {canDeactivateMember(member) ? (
                        deactivatingMemberId === member._id ? (
                          <Spinner size="base" />
                        ) : (
                          <Button
                            label="Deactivate"
                            type="secondary"
                            size="small"
                            onClick={() => deactivateMemberHandler(member)}
                          />
                        )
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card p-3 h-100">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Heading level={4}>Pending Invitations</Heading>
              <span className="text-muted small">{invitations.length} waiting</span>
            </div>
            {invitations.length ? (
              <div className="list-group list-group-flush">
                {invitations.map((invitation) => {
                  const inviteName = getFullName(invitation) || invitation.email;
                  return (
                    <div key={invitation._id} className="list-group-item px-0">
                      <div className="d-flex flex-wrap justify-content-between gap-2">
                        <div>
                          <div className="fw-semibold">{inviteName}</div>
                          <div className="text-muted small">{invitation.email}</div>
                        </div>
                        <span className="badge rounded-pill bg-light text-dark border align-self-start">{getRoleLabel(invitation.role)}</span>
                      </div>
                      <div className="text-muted small mt-1">Expires {new Date(invitation.expiresAt).toLocaleDateString()}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted small border rounded p-3 bg-light">No pending invitations.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessControl;
